<template>
  <div>
    <!-- Dialog for global search results -->
    <Dialog v-model:visible="globalDialogVisible" maximizable modal header="Search Result" :style="{ width: '50rem' }"
      :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
      <div class="flex items-center gap-2 mb-4">
        <input ref="searchInputRef" v-model="globalSearch" @keyup.enter="onGlobalSearch" placeholder="Search all students"
          class="border border-gray-400 px-3 py-1 rounded w-full" type="text" />
        <Button size="small" icon="pi pi-search" @click="onGlobalSearch" />
      </div>

      <ReusablePrimeTable :headers="headers" :data="students" :tableStyle="'min-width: 60rem'" :loading="fetchedStudentsData.loading.value">
        <template #actions="{ row }">
          <Button icon="pi pi-ellipsis-v" class="p-button-text p-button-sm" @click="toggleMenu($event, row)" />
          <Menu ref="menu" :model="getMenuItems(row)" :popup="true" />
        </template>
      </ReusablePrimeTable>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useStudents } from '@/composables/queries/useStudent';
import ReusablePrimeTable from '@/components/common/ReusablePrimeTable.vue';
import type { Student } from '@/types/composition-functions';
import { toStudentSearchRow, type StudentSearchRow } from '@/utils/studentSearchRow';

const route = useRoute();

type StudentRow = StudentSearchRow;

const students = ref<StudentRow[]>([]);

// Initialize student fetching globally, but only auto-fetch on the student admin page
// For other pages, we'll fetch when the search is opened if needed
const studentFilters = computed(() => ({
  filter: {
    search: globalSearch.value || undefined,
  },
  page: 1,
  first: 50,
}));

const { students: fetchedStudentsData, loadStudents, refetchStudents, hasStarted } = useStudents({
  getStudentsOptions: {
    autoFetch: false,
    fetchPolicy: 'network-only',
  },
  studentFilters,
});

// Global search state
const globalSearch = ref('');
const globalDialogVisible = ref(false);
const searchInputRef = ref<HTMLInputElement | null>(null);

const headers = [
  { field: 'id', header: 'Student ID' },
  { field: 'firstname', header: 'First name' },
  { field: 'surname', header: 'Surname' },
  { field: 'gender', header: 'Gender' },
  { field: 'class', header: 'Class' },
  { field: 'email', header: 'Email address' },
  { field: 'actions', header: 'Actions', type: 'actions' },
];

async function onGlobalSearch() {
  if (globalSearch.value.trim()) {
    if (!globalDialogVisible.value) {
      globalDialogVisible.value = true;
    }

    // Trigger server-side search
    if (hasStarted.value) {
      await refetchStudents(studentFilters.value);
    } else {
      await loadStudents(undefined, studentFilters.value);
    }
  }
}

// Keyboard shortcut handler
const handleKeydown = (e: KeyboardEvent) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    globalDialogVisible.value = true;
  }
};

// Handle dialog open to focus input and ensure data is loaded
watch(globalDialogVisible, async(newValue) => {
  if (newValue) {
    await nextTick();
    searchInputRef.value?.focus();

    // Optional: if we want to show initial list (e.g. recent or empty), we can leave it empty
    // OR if we want to show ALL students initially:
    /*
    if (students.value.length === 0) {
       if (!fetchedStudentsData.data.value) {
          loadStudents();
       }
    }
    */
  }
});

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});

watch(
  () => fetchedStudentsData.data.value,
  (newStudents) => {
    if (newStudents) {
      students.value = newStudents.map((s: Student) => toStudentSearchRow(s));
    }
  },
  { immediate: true },
);

type MenuRef = {
  toggle: (event: Event) => void;
};

const menu = ref<MenuRef>();

function toggleMenu(event: Event, row: StudentRow) {
  menu.value?.toggle(event);
}

type MenuItem = {
  label: string;
  icon: string;
  command: () => void;
};

const router = useRouter();

function getMenuItems(row: StudentRow): MenuItem[] {
  return [
    { label: 'View Profile', icon: 'pi pi-eye', command: () => viewRow(row) },
    // { label: 'Edit', icon: 'pi pi-pencil', command: () => editRow(row) }, // Temporarily disabled or redirect to overview
  ];
}

function viewRow(row: StudentRow) {
  globalDialogVisible.value = false;
  router.push({ name: 'StudentOverview', params: { id: row.dbId } });
}

function editRow(row: StudentRow) {
  // For now, redirect to profile where edit is available
  viewRow(row);
}

function deleteRow(row: StudentRow) {
  // For now, redirect to profile where delete is available
  viewRow(row);
}
</script>
